/**
 * Полоса папок над списком чатов.
 *
 * Папки — способ смотреть, а не контейнер владения: чат из папки не пропадает
 * из общего списка. Поэтому «Все» здесь не одна из папок, а отсутствие
 * фильтра.
 *
 * Управление папками доступно старшему менеджеру и администратору: это
 * раскладка работы всей команды, и менять её каждому не следует. А вот
 * раскладывать чаты по папкам может любой, кто правит метаданные чата, —
 * это делается в карточке справа.
 */

import { useState } from 'react';
import type { FolderDto } from '../lib/api.js';
import { useSession } from '../state/session.tsx';
import { PencilIcon, PlusIcon, TrashIcon } from './icons.tsx';
import { cn } from '@/lib/utils';

interface FolderBarProps {
  folders: FolderDto[];
  activeFolderId: string | null;
  onSelect: (folderId: string | null) => void;
  onChanged: () => void;
  /**
   * Фильтры вида списка, показываемые перед папками.
   *
   * Одна строка на всё, что отвечает на вопрос «какие чаты показывать»:
   * непрочитанные, архив, папки. Раньше это были два ряда подряд — фильтры со
   * своей рамкой и папки со своей, — и на узкой панели они съедали высоту,
   * которой не хватало самому списку.
   */
  leading?: React.ReactNode;
}

export function FolderBar({
  folders,
  activeFolderId,
  onSelect,
  onChanged,
  leading,
}: FolderBarProps) {
  const { api, can } = useSession();
  const [busy, setBusy] = useState(false);

  /*
   * Ввод названия — поле в самой полосе, а не модальный запрос.
   *
   * window.prompt в Electron не поддерживается вовсе: вызов бросает
   * исключение, и действие молча не происходит. В браузере он работает, из-за
   * чего расхождение легко не заметить при проверке.
   *
   * null — режим ввода выключен; пустая строка — создание новой папки;
   * идентификатор — переименование существующей.
   */
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const canManage = can('folder.manage');

  const run = async (action: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    try {
      await action();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const submit = (): void => {
    const name = draft.trim();
    if (!name) {
      setEditing(null);
      return;
    }

    const target = editing;
    setEditing(null);
    setDraft('');

    void run(() =>
      target ? api.updateFolder(target, { name }) : api.createFolder({ name }),
    );
  };

  const remove = (folder: FolderDto): void => {
    // Папка исчезает, чаты остаются: связь уходит каскадом, сами переписки не
    // трогаются. Об этом сказано прямо, иначе подтверждение читается страшнее,
    // чем есть.
    const ok = window.confirm(
      `Удалить папку «${folder.name}»?\n\nЧаты останутся на месте — исчезнет только группировка.`,
    );
    if (!ok) return;
    void run(async () => {
      await api.deleteFolder(folder.id);
      if (activeFolderId === folder.id) onSelect(null);
    });
  };

  if (folders.length === 0 && !canManage) return null;

  return (
    <div className="flex flex-none items-center gap-1 overflow-x-auto border-b border-border px-2 py-1.5">
      {leading && (
        <>
          {leading}
          {/* Разделитель, а не пустой промежуток: фильтры и папки отвечают на
              один вопрос, но разными способами, и слитый ряд читался бы как
              один список равнозначных кнопок. */}
          <span
            aria-hidden
            className="mx-0.5 h-4 w-px flex-none bg-border"
          />
        </>
      )}

      <button
        type="button"
        onClick={() => onSelect(null)}
        className={cn(
          'flex-none rounded-full px-2.5 py-1 text-xs transition-colors',
          activeFolderId === null
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-accent',
        )}
      >
        Все
      </button>

      {folders.map((folder) => {
        const active = folder.id === activeFolderId;

        return (
          <span key={folder.id} className="flex flex-none items-center">
            <button
              type="button"
              onClick={() => onSelect(folder.id)}
              className={cn(
                'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs transition-colors',
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent',
              )}
            >
              {folder.name}
              {folder.chatCount !== undefined && folder.chatCount > 0 && (
                <span className={cn('text-[10px]', active ? 'opacity-80' : 'opacity-60')}>
                  {folder.chatCount}
                </span>
              )}
            </button>

            {/* Правка и удаление показываются только у выбранной папки: восемь
                папок с двумя кнопками каждая превратили бы полосу в кашу. */}
            {canManage && active && (
              <>
                <button
                  type="button"
                  aria-label={`Переименовать ${folder.name}`}
                  className="ml-0.5 rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-50"
                  disabled={busy}
                  onClick={() => {
                    setEditing(folder.id);
                    setDraft(folder.name);
                  }}
                >
                  <PencilIcon className="size-3.5" />
                </button>
                <button
                  type="button"
                  aria-label={`Удалить ${folder.name}`}
                  className="rounded p-1 text-muted-foreground hover:text-destructive disabled:opacity-50"
                  disabled={busy}
                  onClick={() => remove(folder)}
                >
                  <TrashIcon className="size-3.5" />
                </button>
              </>
            )}
          </span>
        );
      })}

      {canManage && (
        <button
          type="button"
          aria-label="Создать папку"
          title="Создать папку"
          className="ml-auto flex-none rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
          disabled={busy}
          onClick={() => {
            setEditing('');
            setDraft('');
          }}
        >
          <PlusIcon className="size-4" />
        </button>
      )}

      {editing !== null && (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={submit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') {
              setEditing(null);
              setDraft('');
            }
          }}
          placeholder="Название папки"
          maxLength={60}
          className="h-7 w-40 flex-none rounded-full border border-border bg-background px-3 text-xs outline-none focus:border-ring"
        />
      )}
    </div>
  );
}
